import React, { useState, useMemo, useCallback, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import { FaChevronDown } from "react-icons/fa";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import DateRangePicker from "../../../components/atoms/daterangePicker/DateRangePicker";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  getRequestedListData,
  getStatusData,
  getSpeciesData,
  getProductTypeData,
  getSampleEditData,
  updateSampleEditData,
  getUserRole,
  getUnits,
  formatUserDetails,
  formatProductTypes,
  formatSpecies,
  getProductTrades,
} from "./AssayRequestRecordApi";
import {
  getShippingDetails,
  formatShippingData,
  getShippingDetailsBySample,
} from "../Print Preview/PrintPreviewApi";
import { formatDate } from "../../../utility/Formatdate";
import AssayEditPage from "./AssayEditPage";
import "../../../styles/typography.css";
import "../../Lab Admin Tabs/RequestedList/RequestedList.css";
import "../Print Preview/PrintPreview.css";
import SecondTab from "../../../components/organisms/secondTab";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

function getLocalDateString(date) {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().split("T")[0];
}

const initialAdvancedSearch = {
  groupId: "",
  companyName: "",
  userName: "",
  salesRep: "",
  customerSampleId: "",
  lotNumber: "",
  sampleId: "",
  productType: "All",
  speciesType: "All",
};

function mapAssaySampleResponse(raw) {
  return {
    id: raw.sampleId,
    groupID: raw.groupId,
    locationName: raw.locationName,
    companyName: raw.companyName,
    customerId: raw.customerId,
    unit: raw.unitName,
    status: raw.lastStatus,
    lotNumber: raw.lotNumber,
    samples: [
      {
        id: raw.sampleId,
        sampleId: raw.sampleId,
        barCodeNO: raw.barCodeNumber,
        speciesName: raw.speciesType,
        productTypeName: raw.productType,
        lotNumber: raw.lotNumber,
        customerSampleId: raw.customerId,
        expectedLevel: raw.expectedLevel,
        unit: raw.unitName,
        lastStatus: raw.lastStatus,
        locationName: raw.locationName,
        companyName: raw.companyName,
        base64Barcode: raw.base64Barcode,
      },
    ],
  };
}

const AssayRequestRecord = ({ TabConfig }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(getLocalDateString(oneMonthAgo));
  const [toDate, setToDate] = useState(getLocalDateString(today));
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [requestedListData, setRequestedListData] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [speciesOptions, setSpeciesOptions] = useState([
    { id: "All", name: "All" },
  ]);
  const [productTypeOptions, setProductTypeOptions] = useState([
    { id: "All", name: "All" },
  ]);
  const [advancedSearch, setAdvancedSearch] = useState(initialAdvancedSearch);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [productTypes, setProductTypes] = useState([]);
  const [productTrades, setProductTrades] = useState([]);
  const [feedTypes, setFeedTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [species, setSpecies] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [paginationData, setPaginationData] = useState({});

  const getShippingDetailsByIds = async (groupIds) => {
    try {
      const promises = groupIds.map(async (id) => {
        const response = await getShippingDetails(id);
        return formatShippingData(response);
      });
      const responses = await Promise.all(promises);
      return responses.flat();
    } catch (error) {
      throw error;
    }
  };

  const handlePrint = async () => {
    const selectedRowIdsArray = Array.from(selectedRowIds);
    if (selectedRowIdsArray.length === 0) {
      return;
    }
    try {
      setLoading(true);
      await getShippingDetailsByIds(selectedRowIdsArray);
      const queryString = selectedRowIdsArray.map((id) => `groupId=${id}`).join("&");
      navigate(`/assay-request-record/print-preview?${queryString}`);
    } catch (error) {
      toast.error("Failed to load print data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(
    async (search, currentPage = 1, rowsPerPage = 25) => {
      const payload = {
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterText: "",
        fromDate: fromDate ? new Date(fromDate).toISOString() : "",
        toDate: toDate ? new Date(toDate).toISOString() : "",
        status: statusFilter === "All" ? "" : statusFilter,
        groupId: advancedSearch.groupId ? Number(advancedSearch.groupId) : 0,
        companyName: advancedSearch.companyName || "",
        userName: advancedSearch.userName || "",
        salesRep: advancedSearch.salesRep ? Number(advancedSearch.salesRep) : 0,
        customerSampleId: advancedSearch.customerSampleId || "",
        lotNumber: advancedSearch.lotNumber || "",
        sampleId: advancedSearch.sampleId ? Number(advancedSearch.sampleId) : 0,
        productType:
          advancedSearch.productType && advancedSearch.productType !== "All"
            ? Number(advancedSearch.productType)
            : 0,
        speciesType:
          advancedSearch.speciesType && advancedSearch.speciesType !== "All"
            ? Number(advancedSearch.speciesType)
            : 0,
        state: "",
        locationId: 0,
      };
      try {
        const res = await getRequestedListData(payload);
        const { totalRecords, pageNumber, pageSize, totalPages } = res;
        setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
        setRequestedListData(Array.isArray(res.results) ? res.results : []);
      } catch (error) {
        setRequestedListData([]);
        toast.error("Failed to fetch data. Please try again.");
      }
    },
    [fromDate, toDate, statusFilter, advancedSearch]
  );

  const handleSearch = useCallback(() => {
    fetchData("", 1, 25);
  }, [fromDate, toDate, statusFilter, fetchData]);

  useEffect(() => {
    fetchData("", 1, 25);
    fetchAllAPIData();
  }, [fetchData]);

  useEffect(() => {
    if (fromDate && toDate) {
      handleSearch();
    }
  }, [fromDate, toDate, statusFilter, handleSearch, refreshTrigger]);

  const fetchAllAPIData = async () => {
    try {
      const [
        userRoleRes,
        unitsRes,
        speciesRes,
        productTypesRes,
        productTradesRes,
      ] = await Promise.all([
        getUserRole(),
        getUnits(),
        getSpeciesData(),
        getProductTypeData(),
        getProductTrades(),
      ]);
      setUserRole(formatUserDetails(userRoleRes));
      setUnits(unitsRes);
      setSpecies(formatSpecies(speciesRes));
      setProductTypes(formatProductTypes(productTypesRes));
      setProductTrades(productTradesRes);
    } catch (error) {
      console.error("Failed to fetch API data:", error);
    }
  };

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await getStatusData();
        if (Array.isArray(res) && res.length > 0) {
          setStatusOptions(["All", ...res]);
        }
      } catch {
        setStatusOptions(["All"]);
      }
    };
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    const fetchSpeciesOptions = async () => {
      try {
        const res = await getSpeciesData();
        if (Array.isArray(res) && res.length > 0) {
          setSpeciesOptions([
            { id: "All", name: "All" },
            ...res.map((s) => ({ id: s.speciesId, name: s.name })),
          ]);
        }
      } catch {
        setSpeciesOptions([{ id: "All", name: "All" }]);
      }
    };
    fetchSpeciesOptions();
  }, []);

  useEffect(() => {
    const fetchProductTypeOptions = async () => {
      try {
        const res = await getProductTypeData();
        if (Array.isArray(res) && res.length > 0) {
          setProductTypeOptions([
            { id: "All", name: "All" },
            ...res.map((p) => ({ id: p.productTypeId, name: p.name })),
          ]);
        }
      } catch {
        setProductTypeOptions([{ id: "All", name: "All" }]);
      }
    };
    fetchProductTypeOptions();
  }, []);

  const handleAdvancedInputChange = (e) => {
    const { name, value } = e.target;
    setAdvancedSearch((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleExpand = (groupId) => {
    setExpandedId((prev) => (prev === groupId ? null : groupId));
  };

  const filteredData = useMemo(() => {
    return requestedListData.filter((item) => {
      if (
        searchTerm &&
        ![item.locationName, String(item.groupID), item.userName].some(
          (f) => f && f.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
        return false;
      
      if (statusFilter && statusFilter !== "All") {
        const itemStatus =
          (item.requestedSamples && item.requestedSamples[0] && (item.requestedSamples[0].lastStatus || item.requestedSamples[0].status)) ||
          item.lastStatus ||
          item.status ||
          "";
        if (!itemStatus || String(itemStatus) !== String(statusFilter)) return false;
      }
      return true;
    });
  }, [requestedListData, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = filteredData;
  const isAllSelectedOnPage =
    paginatedData.length > 0 &&
    paginatedData.every((r) => selectedRowIds.has(r.groupID));

  const toggleRowSelection = useCallback((groupID) => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(groupID)) copy.delete(groupID);
      else copy.add(groupID);
      return copy;
    });
  }, []);

  const toggleSelectAllOnPage = () => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (!isAllSelectedOnPage) {
        filteredData.forEach((r) => copy.add(r.groupID));
      } else {
        filteredData.forEach((r) => copy.delete(r.groupID));
      }
      return copy;
    });
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <Form.Check
            type="checkbox"
            checked={isAllSelectedOnPage}
            onChange={toggleSelectAllOnPage}
            aria-label="Select all"
          />
        ),
        accessor: "checkbox",
        disableSort: true,
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selectedRowIds.has(row.groupID)}
            onChange={() => toggleRowSelection(row.groupID)}
            aria-label={`Select ${row.groupID}`}
          />
        ),
      },
      { header: "Request Date", accessor: "requestDate" },
      { header: "Group ID", accessor: "groupID" },
      { header: "Location", accessor: "locationName" },
      { header: "Submitted by", accessor: "submittedBy" },
      {
        header: (
          <span
            style={{ display: "block", textAlign: "center", width: "100%" }}
          >
            Total
          </span>
        ),
        accessor: "total",
        cell: (row) => (
          <div className="total-cell-inner">
            <span className="total-number">{row.total}</span>
            <FaChevronDown
              size={12}
              className="total-arrow"
              style={{
                transition: "transform .2s",
                transform:
                  expandedId === row.groupID ? "rotate(180deg)" : "none",
                cursor: "pointer",
              }}
              onClick={() => toggleExpand(row.groupID)}
            />
          </div>
        ),
      },
    ],
    [
      isAllSelectedOnPage,
      selectedRowIds,
      toggleRowSelection,
      toggleSelectAllOnPage,
      expandedId,
    ]
  );

  const renderExpandedPanel = (data) => {
    const subRowData = data.requestedSamples || [];
    return (
      <tr className="expanded-row" key={`${data.groupID}-expanded`}>
        <td colSpan={7} style={{ padding: 0, background: "#f5f8fb" }}>
          <div style={{ margin: "8px 16px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                background: "#fff",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#586C8F", color: "#fff" }}>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Sample ID
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Barcode No.
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Species
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Product
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Customer Sample ID
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Phibro Lot No
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Range
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Expected Level
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Results
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      borderRight: "1px solid #fff",
                    }}
                  >
                    Status
                  </th>
                  <th style={{ padding: "8px 12px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(subRowData || []).map((row) => (
                  <tr key={row.sampleID}>
                    <td style={{ padding: "10px 12px" }}>{row.sampleId}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.barCodeNO || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.speciesName || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.productTypeName || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.customerSampleId || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.lotNumber || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.range || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.expectedLevel || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.result || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.lotNumber || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {row.lastStatus || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <Button
                        size="sm"
                        variant={
                          row.lastStatus === "Requested"
                            ? "edit-active"
                            : "disabled"
                        }
                        onClick={() => {
                          if (row.lastStatus === "Requested")
                            handleSampleEdit(row);
                        }}
                        disabled={row.lastStatus !== "Requested"}
                        title="Edit sample"
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  const handleAdvanceSearch = () => {
    fetchData("", 1, 25);
    setShowAdvancedSearch(false);
    setAdvancedSearch(initialAdvancedSearch);
  };

  const validateRequiredFields = () => {
    if (!editRowData || !editRowData.samples) return false;
    let hasErrors = false;
    const newErrors = {};
    for (let i = 0; i < editRowData.samples.length; i++) {
      const sample = editRowData.samples[i];
      const sampleId = sample.id || sample.sampleID || sample.sampleId || i + 1;
      const requiredFields = [
        { field: "productTradeName", name: "Product Trade Name" },
        { field: "productType", name: "Product Type" },
        { field: "feedType", name: "Feed Type" },
        { field: "species", name: "Species" },
        { field: "unit", name: "Unit" },
        { field: "expectedLevel", name: "Expected Level" },
        { field: "phibroLots", name: "Phibro Lots" },
        { field: "customerSampleId", name: "Customer Sample ID" },
      ];
      for (const { field, name } of requiredFields) {
        const value = sample[field];
        const fieldKey = `${sampleId}_${field}`;
        if (!value || (typeof value === "string" && value.trim() === "")) {
          newErrors[fieldKey] = `${name} is required`;
          hasErrors = true;
	} else if (field === "expectedLevel") {
          const numValue = Number(value);
          if (isNaN(numValue) || numValue < 0) {
            newErrors[fieldKey] = "Expected Level must be a positive number";
            hasErrors = true;
          }
        }
      }
    }
    setValidationErrors(newErrors);
    return !hasErrors;
  };

  const handleSampleEdit = async (row) => {
    try {
      const sampleID = row.sampleID || row.sampleId;
      if (!sampleID) return;
      const data = await getSampleEditData({ sampleID });
      const structuredData = {
        ...data,
        samples: data.samples
          ? data.samples.map((sample) => ({
              ...sample,
              id: sample.id || sample.sampleID || sample.sampleId,
              expanded: true,
            }))
          : [
              {
                ...data,
                id: data.sampleId || data.sampleID || sampleID,
                expanded: true,
              },
            ],
        currentStatus: data.lastStatus || data.status || "Received",
      };
      setEditRowData(structuredData);
      setValidationErrors({});
      setShowEditModal(true);
    } catch {
      setEditRowData(null);
      toast.error("Failed to fetch sample details.");
    }
  };

  const handleEditDataChange = (updatedData) => {
    setEditRowData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  const handleSampleUpdate = async () => {
    try {
      if (!validateRequiredFields()) return;
      if (!editRowData || !editRowData.samples) {
        toast.error("No data to update.");
        return;
      }
      if (editRowData.sampleId > 0) {
        for (const sample of editRowData.samples) {
          const updatePayload = {
            sampleId: Number(sample.sampleId) || 0,
            productTradeNameId: Number(sample.productTradeName) || 0,
            productTypeId: Number(sample.productType) || 0,
            feedTypeId: Number(sample.feedType) || 0,
            unitId: Number(sample.unit) || 0,
            speciesId: Number(sample.species) || 0,
            locationId:
              Number(
                sample.locationId || sample.locationID || userRole?.locationID
              ) || 0,
            companyId: Number(userRole?.companyId) || 0,
            lotNumber: String(sample.phibroLots || sample.lotNumber || ""),
            customerSampleId: String(sample.customerSampleId || ""),
            referenceNo: String(sample.referenceNo || null),
            barcodeNumber: String(sample.barcodeNumber || null),
            expectedLevel: String(sample.expectedLevel ?? ""),
            cfrMinLevel: sample.cfrMinLevel ? Number(sample.cfrMinLevel) : null,
            cfrMaxLevel: sample.cfrMaxLevel ? Number(sample.cfrMaxLevel) : null,
            loqValue: sample.loqValue || null,
            salesRepId: Number(userRole?.salesRepId) || 0,
            remarks: String(sample.remarks || ""),
            manufactureDate: sample.manufactureDate || null,
          };
          await updateSampleEditData(updatePayload);
        }

        // Fetch and map shipping details response
        const rawShippingData = await getShippingDetailsBySample(
          editRowData.sampleId
        );
        const mappedShippingData = mapAssaySampleResponse(rawShippingData);

        // Optionally update state, but close modal here
        setShowEditModal(false);
        setEditRowData(null);
        setValidationErrors({});
        setRefreshTrigger((prev) => prev + 1);
        navigate(
          `/assay-request-record/print-preview?sampleId=${editRowData.sampleId}`
        );
      } else {
        for (const sample of editRowData.samples) {
          const updatePayload = {
            sampleId: Number(sample.id || sample.sampleID) || 0,
            productTradeNameId: Number(sample.productTradeName) || 0,
            productTypeId: Number(sample.productType) || 0,
            feedTypeId: Number(sample.feedType) || 0,
            unitId: Number(sample.unit) || 0,
            speciesId: Number(sample.species) || 0,
            locationId:
              Number(
                editRowData.locationId ||
                  editRowData.locationID ||
                  userRole?.locationID
              ) || 0,
            companyId: Number(userRole?.companyId) || 0,
            lotNumber: String(sample.phibroLots || sample.lotNumber || ""),
            customerSampleId: String(sample.customerSampleId || ""),
            referenceNo: String(sample.referenceNo || null),
            barcodeNumber: String(sample.barcodeNumber || null),
            expectedLevel: String(sample.expectedLevel ?? ""),
            cfrMinLevel: sample.cfrMinLevel ? Number(sample.cfrMinLevel) : null,
            cfrMaxLevel: sample.cfrMaxLevel ? Number(sample.cfrMaxLevel) : null,
            loqValue: sample.loqValue || null,
            salesRepId: Number(userRole?.salesRepId) || 0,
            remarks: String(sample.remarks || ""),
            manufactureDate: sample.manufactureDate || null,
          };
          await updateSampleEditData(updatePayload);
        }
        const groupId =
          editRowData.groupID ||
          (editRowData.samples && editRowData.samples[0]?.groupID) ||
          (editRowData.samples && editRowData.samples[0]?.groupId) ||
          null;
        setShowEditModal(false);
        setEditRowData(null);
        setValidationErrors({});
        setRefreshTrigger((prev) => prev + 1);
        navigate(`/assay-request-record/print-preview?id=${groupId}`);
      }
    } catch {
      toast.error("Failed to update samples. Please try again.");
    }
  };

  useEffect(() => {
    const sampleId = searchParams.get("sampleId");
    if (sampleId) {
      handleSampleEdit({ sampleID: sampleId });
    }
  }, [searchParams]);

  const fromDateObj = fromDate ? new Date(fromDate) : null;
  const toDateObj = toDate ? new Date(toDate) : null;

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <SecondTab tabConfig={TabConfig} />
      <PageDescriptionBar items={[
        { type: "icon", icon: "✏️", text: "Edit – Modify the details of a submitted assay request" },
        { type: "icon", icon: "☑️", text: "Select – Check rows to perform bulk operations" },
        { type: "separator" },
        { type: "icon", icon: "📅", text: "Date Range – Filter requests by submitted date" },
        { type: "badge", badge: "inactive", text: "Disabled Edit – Requests in a final state cannot be modified" },
      ]} />
      <div className="p-3 typography">
        <div className="d-flex shadow-lg flex-wrap gap-3 mb-2 align-items-center p-3 Tab-color-white">
          <DateRangePicker
            fromDate={fromDateObj}
            toDate={toDateObj}
            onFromDateChange={(date) =>
              setFromDate(date ? date.toISOString().split("T")[0] : "")
            }
            onToDateChange={(date) =>
              setToDate(date ? date.toISOString().split("T")[0] : "")
            }
            fromLabel="From"
            toLabel="To"
            className="me-3"
          />
          <div className="d-flex align-items-center gap-1">
            <label>Select Status</label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 160, height: 44, borderRadius: 5 }}
              size="sm"
              title="Filter by status"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="d-flex align-items-center">
            <Button variant="outlined" onClick={handleSearch} title="Search assay request records">
              Search
            </Button>
          </div>
          <div
            className="line-separator"
            style={{
              width: "1px",
              height: "46px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            }}
          />
          <div className="d-flex align-items-center">
            <Button
              variant="outlined"
              onClick={() => setShowAdvancedSearch(true)}
              title="Open advanced search filters"
            >
              Advance Search
            </Button>
          </div>
          <div className="ms-auto d-flex gap-2">
            <Button
              className="btn-common btn-md default-font-styles"
              variant={selectedRowIds.size > 0 ? "outlined" : "disabled"}
              size="md"
              disabled={selectedRowIds.size === 0}
              onClick={handlePrint}
              title="Print selected records"
            >
              Print
            </Button>
          </div>
        </div>
        <Card>
          <Card.Body className="p-0">
            <SimpleTable
              columns={columns}
              data={filteredData}
              customRowRender={(row) => (
                <React.Fragment key={row.groupID}>
                  <tr>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedRowIds.has(row.groupID)}
                        onChange={() => toggleRowSelection(row.groupID)}
                      />
                    </td>
                    <td>{formatDate(row.requestedSamples[0].requestedDate)}</td>
                    <td>{row.groupID}</td>
                    <td>{row.locationName}</td>
                    <td>{row.userName}</td>
                    <td className="total-cell">
                      <div className="total-cell-inner">
                        <span className="total-number">
                          {row.requestedSamples.length}
                        </span>
                        <FaChevronDown
                          size={14}
                          className="total-arrow"
                          style={{
                            transition: "transform .2s",
                            transform:
                              expandedId === row.groupID
                                ? "rotate(180deg)"
                                : "none",
                            cursor: "pointer",
                          }}
                          onClick={() => toggleExpand(row.groupID)}
                        />
                      </div>
                    </td>
                  </tr>
                  {expandedId === row.groupID && renderExpandedPanel(row)}
                </React.Fragment>
              )}
              paginationData={paginationData}
              searchValue={searchTerm}
              fetchData={fetchData}
            />
          </Card.Body>
        </Card>
      </div>
      <ReusableModal
        show={showAdvancedSearch}
        onHide={() => setShowAdvancedSearch(false)}
        title="Advanced Search"
        size="lg"
        footerClassName="assay-edit-modal-footer"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setShowAdvancedSearch(false),
          },
          {
            text: "Search",
            variant: "active",
            onClick: () => handleAdvanceSearch(),
          },
        ]}
      >
        <Form>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label>Group ID</Form.Label>
              <Form.Control
                name="groupId"
                placeholder="Enter Group ID"
                value={advancedSearch.groupId}
                onChange={handleAdvancedInputChange}
                title="Enter group ID to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                name="companyName"
                placeholder="Enter Company Name"
                value={advancedSearch.companyName}
                onChange={handleAdvancedInputChange}
                title="Enter company name to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>User Name</Form.Label>
              <Form.Control
                name="userName"
                placeholder="Enter User Name"
                value={advancedSearch.userName}
                onChange={handleAdvancedInputChange}
                title="Enter user name to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Sales Rep</Form.Label>
              <Form.Control
                name="salesRep"
                placeholder="Enter Sales Rep"
                value={advancedSearch.salesRep}
                onChange={handleAdvancedInputChange}
                title="Enter sales rep to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Customer Sample ID</Form.Label>
              <Form.Control
                name="customerSampleId"
                placeholder="Enter Customer Sample ID"
                value={advancedSearch.customerSampleId}
                onChange={handleAdvancedInputChange}
                title="Enter customer sample ID to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Lot Number</Form.Label>
              <Form.Control
                name="lotNumber"
                placeholder="Enter Lot Number"
                value={advancedSearch.lotNumber}
                onChange={handleAdvancedInputChange}
                title="Enter lot number to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Sample ID</Form.Label>
              <Form.Control
                name="sampleId"
                placeholder="Enter Sample ID"
                value={advancedSearch.sampleId}
                onChange={handleAdvancedInputChange}
                title="Enter sample ID to search"
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Product</Form.Label>
              <Form.Select
                name="productType"
                value={advancedSearch.productType}
                onChange={handleAdvancedInputChange}
                title="Filter by product type"
              >
                {productTypeOptions.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Species</Form.Label>
              <Form.Select
                name="speciesType"
                value={advancedSearch.speciesType}
                onChange={handleAdvancedInputChange}
                title="Filter by species"
              >
                {speciesOptions.map((species) => (
                  <option key={species.id} value={species.id}>
                    {species.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Form>
      </ReusableModal>
      <ReusableModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditRowData(null);
          setValidationErrors({});
        }}
        title="Edit Samples"
        size="xl"
        footerClassName="assay-edit-modal-footer"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => {
              setShowEditModal(false);
              setEditRowData(null);
              setValidationErrors({});
            },
          },
          {
            text: "Update",
            variant: "active",
            onClick: () => handleSampleUpdate(),
          },
        ]}
      >
        <AssayEditPage
          data={editRowData}
          onDataChange={handleEditDataChange}
          productTypes={productTypes}
          productTrades={productTrades}
          feedTypes={feedTypes}
          species={species}
          units={units}
          locations={[]}
          userRole={userRole}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
        />
      </ReusableModal>
    </div>
  );
};

export default AssayRequestRecord;